import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, Button } from '@cityelectricalfactors/ui-components';

import { SearchIcon, ChevronIcon, CircleXmark } from 'assets/common_svgs';
import ApprovalsGroupModalUser from './approvals_group_modal_user';
import { groupActions } from './approvals_group_reducer';
import {
  white,
  grey30,
  grey70,
} from '../../../../../assets/stylesheets/v2/abstracts/_variables.scss';

const ApprovalsGroupSelectModal = ({
  options,
  closeModal,
  approvalGroupDispatch,
}) => {
  const {
    showModal,
    title,
    cta,
    users,
    userCount,
    modalSelectionCount,
    currentSelectionCount,
    selectAllUsers,
    orderByName,
    orderByLimit,
    actionType,
  } = options;

  const [searchTerm, setSearchTerm] = useState('');

  const noUsersSelected =
    currentSelectionCount === 0 && modalSelectionCount === 0;

  const filteredUsers = users.filter(user =>
    `${user.forename} ${user.surname}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  const searchInputHandler = event => {
    setSearchTerm(event.target.value);
  };

  const clearSearchHandler = () => {
    setSearchTerm('');
  };

  const selectAllHandler = () => {
    approvalGroupDispatch({
      type: groupActions.SELECT_ALL_USER_MODAL,
      payload: filteredUsers,
    });
  };

  const clearAllHandler = () => {
    approvalGroupDispatch({ type: groupActions.CLEAR_ALL_USER_MODAL });
  };

  const selectUserHandler = customerId => {
    approvalGroupDispatch({
      type: groupActions.UPDATE_USER_MODAL,
      payload: customerId,
    });
    approvalGroupDispatch({
      type: groupActions.SELECT_ALL_STATUS_MODAL,
      payload: filteredUsers,
    });
  };

  const addUsersHandler = () => {
    approvalGroupDispatch({ type: actionType, payload: users });
    setSearchTerm('');
    closeModal();
  };

  const nameOrderHandler = () =>
    approvalGroupDispatch({ type: groupActions.SORT_BY_NAME_MODAL });

  const limitOrderHandler = () =>
    approvalGroupDispatch({ type: groupActions.SORT_BY_LIMIT_MODAL });

  const closeModalHandler = () => {
    closeModal();
    setSearchTerm('');
  };

  const limitTypes = {
    'Standard Users': 'Credit',
    Approvers: 'Approval',
    default: '',
  };
  const limitType = limitTypes[title] || limitTypes.default;

  const createSkeletonUsers = () => {
    const skeletonCount = userCount < 5 ? userCount : 5;
    const skeletonUsers = [];
    for (let i = 0; i < skeletonCount; i++) {
      skeletonUsers.push(<ApprovalsGroupModalUser key={i} skeleton />);
    }
    return skeletonUsers;
  };

  useEffect(() => {
    approvalGroupDispatch({
      type: groupActions.SELECT_ALL_STATUS_MODAL,
      payload: filteredUsers,
    });
  }, [searchTerm]);

  return (
    <SelectModal isOpen={showModal} closeModal={closeModalHandler}>
      <h3 className="ff-r fw-700 fs-16 tc-grey-70 lh-1 mb-16">{`Select ${title}`}</h3>
      <div className="user-search width-100 fl ai-c mb-18">
        <div className="user-search__icon-wrapper fl jc-fe ai-c pr-5 bs-bb">
          <SearchIcon fillColor={grey30} width={15} height={15} />
        </div>
        <input
          id="search users"
          aria-label="search users"
          type="text"
          className="ff-r fw-400 fs-14 tc-grey-70"
          onChange={searchInputHandler}
          value={searchTerm}
          placeholder="Search users"
        />
        {searchTerm.length > 0 && (
          <ClearSearchBtn
            label="clear search"
            handleClick={clearSearchHandler}
            className="bg-transparent p-0">
            <CircleXmark
              width="20"
              height="20"
              circleFillColor={grey70}
              xmarkFillColor={white}
            />
          </ClearSearchBtn>
        )}
      </div>
      <div className="user-filters fl ai-c jc-fs mb-8">
        <div className="user__checkbox-wrapper fl jc-c ai-c">
          <input
            aria-label="select all users"
            type="checkbox"
            onChange={selectAllHandler}
            checked={selectAllUsers ? 'checked' : ''}
          />
        </div>
        <OrderByNameBtn
          className="bg-transparent p-0"
          handleClick={nameOrderHandler}>
          <p className="ff-r fs-13 lh-1 tc-grey-70 mb-0 mr-6">Name</p>
          <NameIconWrapper orderByName={orderByName} className="fl fl-col jc-c">
            <ChevronIcon height={7} width={11} />
          </NameIconWrapper>
        </OrderByNameBtn>
        {limitType.length > 0 && (
          <OrderByLimitBtn
            className="bg-transparent p-0 ml-auto"
            handleClick={limitOrderHandler}>
            <p className="ff-r fs-13 lh-1 tc-grey-70 mb-0 mr-6">{`${limitType} Limit`}</p>
            <LimitIconWrapper
              orderByLimit={orderByLimit}
              className="fl fl-col jc-c">
              <ChevronIcon height={7} width={11} />
            </LimitIconWrapper>
          </OrderByLimitBtn>
        )}
      </div>
      <div className="user-list__wrapper mb-20">
        <ul className="m-0 p-0">
          {users.length === userCount && filteredUsers.length === 0 && (
            <li className="user__wrapper pt-15 pl-12">
              <p>No users found</p>
            </li>
          )}
          {filteredUsers.length > 0 &&
            filteredUsers.map((user, index) => (
              <ApprovalsGroupModalUser
                key={user.customer_id}
                index={index}
                user={user}
                limitType={limitType}
                selectUserHandler={selectUserHandler}
              />
            ))}
          {users.length !== userCount && createSkeletonUsers()}
        </ul>
      </div>
      <div className="fl">
        {modalSelectionCount > 0 && (
          <div className="fl ai-c">
            <p className="ff-r fs-14 tc-grey-70 lh-1-1 mb-0">
              {`${modalSelectionCount} users selected`}
            </p>
            <Button
              handleClick={clearAllHandler}
              className="bg-transparent fw-500 tc-cef-red td-u ml-16 p-0">
              Clear selection
            </Button>
          </div>
        )}
        <div className="fl ai-c ml-auto">
          <Button
            handleClick={closeModalHandler}
            className="bg-transparent tc-grey-70 ml-16 p-0">
            Cancel
          </Button>
          <AddUsersButton
            handleClick={addUsersHandler}
            className={`${
              noUsersSelected ? 'bg-grey-5 tc-grey-50' : 'bg-cef-red tc-white'
            } ml-20`}
            disabled={noUsersSelected}>
            {currentSelectionCount === 0 ? cta : 'Save Changes'}
          </AddUsersButton>
        </div>
      </div>
    </SelectModal>
  );
};

const ClearSearchBtn = styled(Button)`
  width: 44px;
`;

const OrderByNameBtn = styled(Button)`
  min-height: 20px;
`;

const NameIconWrapper = styled.div`
  ${({ orderByName }) => orderByName && 'transform: rotate(180deg);'}
`;

const OrderByLimitBtn = styled(Button)`
  width: 127px;
  min-height: 20px;
  justify-content: flex-start;
`;

const LimitIconWrapper = styled.div`
  ${({ orderByLimit }) => orderByLimit && 'transform: rotate(180deg);'}
`;

const SelectModal = styled(Modal)`
  background-color: rgba(0, 0, 0, 0.7);
  & > div {
    box-sizing: border-box;
    width: 100%;
    max-width: 600px;
    height: 476px;
    padding: 21px 20px 22px 20px;
  }
`;

const AddUsersButton = styled(Button)`
  width: 150px;
  height: 40px;
`;

export default ApprovalsGroupSelectModal;
